import { Capacitor } from "@capacitor/core";

/**
 * 새 버전 배포 → 열려 있는 화면을 한 번 새로 고친다(2026-09-24).
 *
 * 서비스 워커(vite-plugin-pwa autoUpdate: skipWaiting + clientsClaim)는 새 sw.js 를 받자마자 활성화되지만,
 * **이미 떠 있는 화면은 옛 JS 그대로**다. 그래서 배포 뒤 새로고침을 두 번 해야 새 버전이 떴고(첫 번째는
 * 옛 캐시가 응답하고 그 사이 새 SW 가 설치된다), 켜 둔 관제 화면은 며칠씩 옛 버전이었다 —
 * 「웹 수정 배포한 거 맞아?」 지적의 원인. 이제
 *   · 새 SW 가 이 화면을 넘겨받으면(controllerchange) 한 번 새로 고친다.
 *     입력 중이면(입력칸·편집 영역에 포커스) 포커스가 빠질 때까지, 최대 10분 미룬다.
 *   · 켜 둔 화면도 30분마다 sw.js 를 다시 확인한다(브라우저는 페이지 이동 때만 확인한다).
 * ⛔ 첫 방문(원래 SW 가 없던 화면)의 clientsClaim 도 controllerchange 를 일으킨다 — 그때는 새 버전이
 *    아니므로 새로 고치지 않는다. 앱(Capacitor)은 자산이 설치본에 들어 있어 건너뛴다.
 */
const CHECK_EVERY_MS = 30 * 60_000;
const MAX_DEFER_MS = 10 * 60_000;

function isEditing(): boolean {
  const el = document.activeElement as HTMLElement | null;
  if (!el) return false;
  const tag = el.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || el.isContentEditable;
}

export function installServiceWorkerAutoRefresh(): void {
  if (typeof window === "undefined" || !("serviceWorker" in navigator)) return;
  if (Capacitor.isNativePlatform()) return;
  const sw = navigator.serviceWorker;
  const hadController = !!sw.controller;
  let reloaded = false;

  const reloadNow = () => {
    if (reloaded) return;
    reloaded = true;
    window.location.reload();
  };
  const reloadWhenIdle = () => {
    if (!isEditing()) { reloadNow(); return; }
    const onBlur = () => setTimeout(() => { if (!isEditing()) reloadNow(); }, 1_000);
    window.addEventListener("focusout", onBlur);
    setTimeout(reloadNow, MAX_DEFER_MS);
  };

  sw.addEventListener("controllerchange", () => {
    if (!hadController) return;
    reloadWhenIdle();
  });

  void sw.ready
    .then((reg) => {
      window.setInterval(() => { void reg.update().catch(() => undefined); }, CHECK_EVERY_MS);
    })
    .catch(() => undefined);
}
